import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { fetch, pessimisticUpdate } from '@ngrx/router-store/data-persistence';
import { map, tap } from 'rxjs';
import { DeliveryAddressService } from '../services/delivery-address.service';

import { NavController } from '@ionic/angular';
import { DeliveryAddressesActions } from './delivery-address.actions';
import { ActivatedRoute } from '@angular/router';
import { getBoolean, getErrorMessage, RouteUtil } from '@us/web/shared/utils';
import { CartActions } from '@us/web/cart/data-access';
import { Store } from '@ngrx/store';
import { MessageService } from '@us/web/shared/feature';

@Injectable()
export class DeliveryAddressEffects {
    private readonly actions$ = inject(Actions);
    private readonly deliveryAddressService = inject(DeliveryAddressService);
    private readonly navController = inject(NavController);
    private readonly route = inject(ActivatedRoute);
    private readonly store = inject(Store);
    private readonly message = inject(MessageService);

    loadDeliveryAddresses$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(DeliveryAddressesActions.loadDeliveryAddresses),
            fetch({
                run: () =>
                    this.deliveryAddressService
                        .getAll()
                        .pipe(
                            map(deliveryAddresses =>
                                DeliveryAddressesActions.loadDeliveryAddressesSuccess({ deliveryAddresses })
                            )
                        ),
                onError: (action, error) => {
                    console.error('Error', error);
                    return DeliveryAddressesActions.loadDeliveryAddressesFailure({ error: getErrorMessage(error) });
                }
            })
        );
    });

    createDeliveryAddress$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(DeliveryAddressesActions.createDeliveryAddress),
            pessimisticUpdate({
                run: ({ deliveryAddress }) =>
                    this.deliveryAddressService
                        .create(deliveryAddress)
                        .pipe(
                            map(deliveryAddress =>
                                DeliveryAddressesActions.createDeliveryAddressSuccess({ deliveryAddress })
                            )
                        ),
                onError: error => {
                    console.error('Error', error);
                    return DeliveryAddressesActions.createDeliveryAddressFailure({ error: getErrorMessage(error) });
                }
            })
        );
    });

    createDeliveryAddressSuccess$ = createEffect(
        () => {
            return this.actions$.pipe(
                ofType(DeliveryAddressesActions.createDeliveryAddressSuccess),
                tap(async ({ deliveryAddress }) => {
                    const returnUrl = this.route.snapshot.queryParamMap.get('returnUrl');
                    const addressSelection = getBoolean(this.route.snapshot.queryParamMap.get('addressSelection'));
                    if (addressSelection) {
                        this.store.dispatch(CartActions.updateCart({ receiver: deliveryAddress }));
                    }

                    await this.navController.navigateBack(returnUrl || RouteUtil.getDeliveryAddressesRouteUrl());
                })
            );
        },
        { dispatch: false }
    );

    deleteDeliveryAddress$ = createEffect(
        () => {
            return this.actions$.pipe(
                ofType(DeliveryAddressesActions.deleteDeliveryAddress),
                tap(async ({ id }) => {
                    this.deliveryAddressService.delete(id).subscribe({
                        next: res => {
                            this.store.dispatch(DeliveryAddressesActions.loadDeliveryAddresses());
                        },
                        error: res => {
                            this.message.error(res.error.error_description ?? 'Có lỗi xảy ra');
                        }
                    });
                })
            );
        },
        { dispatch: false }
    );
}
